import { useEffect, useRef, useState } from "react";
import { Droplets, Sprout, FlaskConical, Mountain, ScanLine, Radar, ClipboardCheck, Crosshair, CalendarCheck, Siren, ArrowUpRight, X } from "lucide-react";
import { SERVICIOS, waLink, track } from "@/data/content";
import { Reveal, SectionTag, TituloLineas } from "@/components/Reveal";
import Desplegable from "@/components/Desplegable";
import Calendario from "@/components/Calendario";
import AgendarCampana from "@/components/AgendarCampana";
import { useCertero } from "@/context/CerteroContext";

const ICONS = {
  droplets: Droplets, sprout: Sprout, flask: FlaskConical, mountain: Mountain,
  scan: ScanLine, radar: Radar, clipboard: ClipboardCheck, crosshair: Crosshair,
};

// Ventana modal genérica del bloque (calendario y campaña).
function VentanaModal({ abierto, onClose, titulo, servicio, testid, children }) {
  useEffect(() => {
    if (!abierto) return;
    const f = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", f);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", f);
      document.body.style.overflow = "";
    };
  }, [abierto, onClose]);
  if (!abierto) return null;
  return (
    <div data-testid={testid} onClick={onClose} className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-sm">
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[88vh] w-full max-w-3xl overflow-auto border border-hud bg-abyss p-6 sm:p-8"
      >
        <button
          data-testid={`${testid}-close`}
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute right-4 top-4 z-10 inline-flex items-center gap-2 rounded-full border border-hud bg-abyss px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist transition-colors duration-300 hover:border-certero hover:text-certero"
        >
          Cerrar <X className="h-4 w-4" />
        </button>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-certero">
          {servicio ? `Servicio elegido: ${servicio.t}` : titulo}
        </p>
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

export default function Servicios() {
  const { setCalServicio } = useCertero();
  const [calAbierto, setCalAbierto] = useState(false);
  const [campanaAbierta, setCampanaAbierta] = useState(false);
  const [abierto, setAbierto] = useState(null); // una sola tarjeta abierta por vez
  const [servicioElegido, setServicioElegido] = useState(null);

  const abrirCalendario = (s) => {
    setCalServicio(s.id);
    setServicioElegido(s);
    setCalAbierto(true);
    track("agendar_servicio_click", { servicio: s.t });
  };

  return (
    <section id="servicios" className="scroll-mt-20 bg-abyss px-4 py-14 sm:px-8 sm:py-20 lg:px-12 lg:py-32">
      <div className="mx-auto max-w-[1400px]">
        <Reveal>
          <SectionTag>Servicios con drones</SectionTag>
          <TituloLineas
            lineas={["Cada aplicación,", "medida y ejecutada"]}
            className="mt-5 max-w-2xl font-display text-2xl font-bold uppercase leading-tight tracking-tight text-mist sm:text-3xl lg:text-4xl"
          />
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-smoke sm:text-base">
            Toque un servicio para ver en qué consiste y agendarlo.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
          {SERVICIOS.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.id} delay={Math.min(0.3, i * 0.05)}>
                <Desplegable
                  id={s.id}
                  titulo={s.t}
                  icono={Icon}
                  abierto={abierto === s.id}
                  onToggle={(id) => setAbierto((a) => (a === id ? null : id))}
                  testid={`service-desplegable-${s.id}`}
                >
                  <p className="flex-1 text-sm leading-relaxed text-smoke">{s.d}</p>
                  <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
                    <button
                      data-testid={`service-btn-agendar-${s.id}`}
                      onClick={() => abrirCalendario(s)}
                      className="inline-flex items-center gap-2 self-start font-display text-xs font-bold uppercase tracking-[0.15em] text-certero transition-colors duration-300 hover:text-[#4bc443]"
                    >
                      <CalendarCheck className="h-4 w-4" /> Agendar servicio
                    </button>
                    <a
                      data-testid={`service-btn-cotizar-${s.id}`}
                      href={waLink(`Hola CERTERO SRL, quiero cotizar el servicio de ${s.t} con drones.`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => track("cotizar_servicio_click", { servicio: s.t })}
                      className="inline-flex items-center gap-2 self-start font-mono text-[11px] uppercase tracking-[0.2em] text-mist/70 transition-colors duration-300 hover:text-certero"
                    >
                      Cotizar <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </Desplegable>
              </Reveal>
            );
          })}
        </div>

        {/* Botones grandes del bloque */}
        <Reveal delay={0.1} className="mt-12">
          <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
            <button
              data-testid="servicios-btn-agendar-campana"
              onClick={() => { track("agendar_campana_click", { origen: "portada" }); setCampanaAbierta(true); }}
              className="inline-flex h-14 items-center justify-center gap-3 rounded-full bg-certero px-9 font-display text-sm font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,transform] duration-300 hover:bg-[#4bc443] active:scale-95"
            >
              <CalendarCheck className="h-4 w-4" /> Agendar campaña
            </button>
            <a
              data-testid="servicios-btn-emergencia"
              href={waLink("EMERGENCIA EN CAMPO — Hola CERTERO SRL, tengo una emergencia con mi dron y necesito atención inmediata. Le adjunto fotos o video del problema.")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("emergencia_whatsapp", { origen: "servicios" })}
              className="inline-flex h-14 items-center justify-center gap-3 rounded-full border border-red-400/70 px-9 font-display text-sm font-bold uppercase tracking-[0.15em] text-red-300 transition-[background-color,color] duration-300 hover:bg-red-500 hover:text-white"
            >
              <Siren className="h-4 w-4" /> Emergencia
            </a>
          </div>
        </Reveal>
      </div>

      <VentanaModal abierto={calAbierto} onClose={() => setCalAbierto(false)} servicio={servicioElegido} testid="calendario-modal">
        <Calendario />
      </VentanaModal>

      <VentanaModal abierto={campanaAbierta} onClose={() => setCampanaAbierta(false)} titulo="Agendar campaña" testid="campana-modal">
        <AgendarCampana />
      </VentanaModal>
    </section>
  );
}
